import { useRef, useState, useEffect } from "react";
import { Play, Pause, Download } from "lucide-react";

function fmt(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function AudioPlayer({ src, title, subtitle, downloadHref, testid }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDur(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (playing) a.pause();
    else a.play();
    setPlaying(!playing);
  };

  const seek = (e) => {
    const v = Number(e.target.value);
    audioRef.current.currentTime = v;
    setTime(v);
  };

  return (
    <div className="bg-obsidian text-white rounded-2xl p-4 sm:p-5 grain relative overflow-hidden" data-testid={testid}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          data-testid={`${testid}-play-button`}
          className="w-11 h-11 shrink-0 rounded-full bg-gold text-obsidian flex items-center justify-center hover:scale-105 transition-transform"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <p className="font-medium truncate text-sm">{title}</p>
            <div className="flex items-end gap-[3px] h-4 shrink-0">
              {[0, 1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className={`w-[3px] h-4 rounded-full ${playing ? "eq-bar bg-gold" : "bg-white/25"}`}
                  style={{ animationDelay: `${i * 0.13}s` }}
                />
              ))}
            </div>
          </div>
          {subtitle && <p className="text-xs text-white/50 truncate">{subtitle}</p>}
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[11px] font-mono text-white/60 w-9">{fmt(time)}</span>
            <input
              type="range"
              min={0}
              max={dur || 0}
              step={0.1}
              value={time}
              onChange={seek}
              data-testid={`${testid}-seek-slider`}
              className="flex-1 h-1 accent-[#D4AF37] cursor-pointer"
            />
            <span className="text-[11px] font-mono text-white/60 w-9 text-right">{fmt(dur)}</span>
          </div>
        </div>
        {downloadHref && (
          <a
            href={downloadHref}
            data-testid={`${testid}-download-button`}
            className="shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors"
            aria-label="Download"
          >
            <Download size={15} />
          </a>
        )}
      </div>
    </div>
  );
}
